import React, { useState, useRef, lazy, Component } from 'react';
import { ChevronLeft, ChevronRight, BookOpenIcon, ClipboardListIcon, PuzzleIcon, BookmarkIcon, FileTextIcon, ArrowRightIcon, EyeIcon } from 'lucide-react';
type Item = {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  desc: string;
  specs: string[];
  pages: string;
};
const items: Item[] = [{
  icon: BookOpenIcon,
  title: '5 Volumi · 1ª-5ª classe',
  desc: 'Spiegazioni, esercizi progressivi e mappe per ogni classe.',
  specs: ['5 PDF separati', 'Difficoltà progressiva'],
  pages: '200+ pagine'
}, {
  icon: ClipboardListIcon,
  title: 'Registro Valutazione',
  desc: 'Griglie già compilate e conformi alla normativa descrittiva.',
  specs: ['Editabile in Word', 'Conforme normativa'],
  pages: '40+ pagine'
}, {
  icon: PuzzleIcon,
  title: 'Laboratori "Matematica Viva"',
  desc: 'Giochi logici, coding e attività pratiche per la classe.',
  specs: ['100+ attività', 'Stampabili A4'],
  pages: '80+ pagine'
}, {
  icon: BookmarkIcon,
  title: 'Formule e Regole',
  desc: 'Lapbook e mappe da tenere sempre nel quaderno.',
  specs: ['Lapbook stampabili', 'Versione DSA inclusa'],
  pages: '30+ pagine'
}];
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/xmbsvKGSZxrhaxfxMPPLNH/Captura-de-pantalla-2026-05-01-a-las-4.22.43_p.-m..webp',
  alt: 'Scheda 1.1 — Il Valore Posizionale fino al Milione',
  label: 'Valore Posizionale',
  chapter: 'Volume 5ª · Unità 1.1'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/coYL52BFQyaiXHK2q6Qnjj/Captura-de-pantalla-2026-05-01-a-las-4.21.05_p.-m..webp',
  alt: 'Schede 2.3 e 2.4 — Sottrazione senza e con Prestito',
  label: 'Sottrazione con prestito',
  chapter: 'Volume 3ª · Unità 2.3'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/hEKDTN94M71gHGraVyzd3o/Captura-de-pantalla-2026-05-01-a-las-4.22.26_p.-m..webp',
  alt: 'Verifica — Unità 2: Addizione e Sottrazione',
  label: 'Verifica pronta',
  chapter: 'Volume 3ª · Unità 2'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/voTDyLibUm2dHDFFX5i9vQ/Captura-de-pantalla-2026-05-01-a-las-4.23.28_p.-m..webp',
  alt: 'Scheda 8.2 — Problemi di Logica e Ragionamento',
  label: 'Problemi di logica',
  chapter: 'Volume 4ª · Unità 8.2'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gPTrBvD4wN91oFAcb4gH4T/Captura-de-pantalla-2026-05-01-a-las-4.23.28_p.-m._1.webp',
  alt: 'Scheda 8.2 — Problemi di Logica e Ragionamento (dettaglio)',
  label: 'Ragionamento avanzato',
  chapter: 'Volume 5ª · Unità 8.2'
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const handleScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const center = container.scrollLeft + container.clientWidth / 2;
      let minDistance = Infinity;
      let active = 0;
      for (let i = 0; i < container.children.length; i++) {
        const child = container.children[i] as HTMLElement;
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const distance = Math.abs(center - childCenter);
        if (distance < minDistance) {
          minDistance = distance;
          active = i;
        }
      }
      setActiveIndex(active);
    });
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => activeIndex > 0 && scrollToIndex(activeIndex - 1);
  const scrollNext = () => activeIndex < carouselImages.length - 1 && scrollToIndex(activeIndex + 1);
  return <section className="w-full bg-bg-dark py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-75">
      {/* Background accents */}
      <div className="absolute top-1/4 -right-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(234,88,12,0.08) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-76" />
      <div className="absolute bottom-1/4 -left-32 w-96 h-96 rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-77" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-78">
        {/* Section header — compact */}
        <div className="text-center mb-8 sm:mb-10" data-id="element-79">
          <span className="inline-flex items-center gap-2 text-primary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full mb-3 sm:mb-4" data-id="element-80">
            📦 Cosa c'è dentro
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.05] mb-2 sm:mb-3" data-id="element-81">
            <span className="text-primary" data-id="element-82">350+ pagine</span> già pronte.
          </h2>
          <p className="text-text-sec text-sm sm:text-lg max-w-xl mx-auto leading-snug" data-id="element-83">
            PDF + Word, stampabili A4 o LIM.{' '}
            <span className="text-text-main font-bold" data-id="element-84">Da oggi stesso.</span>
          </p>
        </div>

        {/* Items grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-10 sm:mb-14" data-id="element-85">
          {items.map((item, i) => {
          const Icon = item.icon;
          return <article key={i} className="relative bg-bg-alt rounded-2xl border border-border-light p-4 sm:p-5 hover:border-primary/50 hover:-translate-y-0.5 transition-all duration-300 group" data-id="element-86">
                {/* Pages badge */}
                <div className="absolute top-4 right-4 inline-flex items-center gap-1 bg-bg-dark/60 border border-white/10 px-2 py-0.5 rounded-full" data-id="element-87">
                  <FileTextIcon className="w-3 h-3 text-primary" strokeWidth={2.5} data-id="element-88" />
                  <span className="text-text-main text-[10px] sm:text-xs font-black tracking-tight" data-id="element-89">
                    {item.pages}
                  </span>
                </div>

                <div className="flex items-start gap-3 sm:gap-4" data-id="element-90">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 group-hover:border-primary/60 transition-colors" data-id="element-91">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={2.25} data-id="element-92" />
                  </div>
                  <div className="min-w-0 flex-1 pr-16" data-id="element-93">
                    <h3 className="text-base sm:text-lg font-black text-text-main leading-tight mb-1" data-id="element-94">
                      {item.title}
                    </h3>
                    <p className="text-text-sec text-[13px] sm:text-sm leading-snug mb-2.5" data-id="element-95">
                      {item.desc}
                    </p>
                    <div className="flex flex-wrap gap-1" data-id="element-96">
                      {item.specs.map((spec, j) => <span key={j} className="inline-flex items-center text-text-sec text-[10px] sm:text-[11px] font-bold bg-bg-dark/60 border border-white/10 px-2 py-0.5 rounded-md" data-id="element-97">
                          {spec}
                        </span>)}
                    </div>
                  </div>
                </div>
              </article>;
        })}
        </div>

        {/* Carousel header — single eyebrow only */}
        <div className="text-center mb-5 sm:mb-6" data-id="element-98">
          <span className="inline-flex items-center gap-2 text-secondary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-secondary/10 border border-secondary/20 px-4 py-1.5 rounded-full" data-id="element-99">
            <EyeIcon className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-100" />
            Anteprima reale del materiale
          </span>
        </div>

        {/* Carousel */}
        <div className="relative" data-id="element-101">
          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-102">
            {/* Navigation buttons */}
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-bg-main border border-border-light hover:border-primary/50 p-3 rounded-full text-text-main transition-all shadow-lg ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Precedente" data-id="element-103">
              <ChevronLeft className="w-6 h-6" data-id="element-104" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-bg-main border border-border-light hover:border-primary/50 p-3 rounded-full text-text-main transition-all shadow-lg ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Successivo" data-id="element-105">
              <ChevronRight className="w-6 h-6" data-id="element-106" />
            </button>

            {/* Slides */}
            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-180px)] pb-6 pt-4" data-id="element-107">
              {carouselImages.map((img, i) => <div key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[360px] bg-bg-alt border border-border-light rounded-2xl overflow-hidden flex flex-col shadow-[0_20px_50px_rgba(0,0,0,0.4)] transition-all duration-300 ${activeIndex === i ? 'scale-100 opacity-100 border-primary/50' : 'scale-95 opacity-50'}`} data-id="element-108">
                  <div className="p-2 sm:p-3 flex items-center justify-center bg-white/5" data-id="element-109">
                    <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[380px] sm:max-h-[420px] object-contain rounded-xl" data-id="element-110" />
                  </div>
                  <div className="px-4 py-3 border-t border-border-light bg-bg-dark/40" data-id="element-111">
                    <p className="text-[10px] sm:text-[11px] text-text-sec font-bold uppercase tracking-wider mb-0.5" data-id="element-112">
                      {img.chapter}
                    </p>
                    <p className="text-text-main font-black text-sm sm:text-base leading-tight" data-id="element-113">
                      {img.label}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Counter + Dots */}
          <div className="flex items-center justify-center gap-3 mt-2" data-id="element-114">
            <span className="text-text-sec text-xs font-bold tracking-wider tabular-nums" data-id="element-115">
              {String(activeIndex + 1).padStart(2, '0')}
              <span className="text-text-sec/40" data-id="element-116">
                {' '}
                / {String(carouselImages.length).padStart(2, '0')}
              </span>
            </span>
            <div className="flex items-center" data-id="element-117">
              {carouselImages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className="flex items-center justify-center min-w-[36px] min-h-[36px] p-2" aria-label={`Vai alla scheda ${i + 1}`} data-id="element-118">
                  <span className={`block h-1.5 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8' : 'bg-border-light hover:bg-text-sec w-1.5'}`} data-id="element-119" />
                </button>)}
            </div>
          </div>

          <p className="text-center text-text-sec text-xs mt-2 sm:hidden flex items-center justify-center gap-1.5" data-id="element-120">
            <ChevronLeft className="w-3.5 h-3.5 animate-pulse-composited" data-id="element-121" />
            Scorri per vedere di più
            <ChevronRight className="w-3.5 h-3.5 animate-pulse-composited" data-id="element-122" />
          </p>
        </div>

        {/* Final CTA — tight */}
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 bg-bg-alt border border-border-light rounded-2xl px-5 py-4 sm:py-5" data-id="element-123">
          <p className="text-text-main text-base sm:text-lg font-semibold text-center sm:text-left leading-snug" data-id="element-124">
            ⚡ Nel tuo computer in{' '}
            <span className="text-cta-green font-black" data-id="element-125">30 secondi</span>.
          </p>
          <a href="#pricing" style={{
          boxShadow: '0 0 20px rgba(57,255,20,0.55), 0 8px 24px rgba(57,255,20,0.35)'
        }} className="inline-flex items-center justify-center gap-2 font-black text-sm sm:text-base px-6 py-3 sm:px-7 sm:py-3.5 rounded-xl border-2 border-white/30 whitespace-nowrap uppercase tracking-tight transition-transform hover:scale-[1.03] active:scale-[0.98] text-[#FFFFFF] bg-[#22C55E] w-full sm:w-auto" data-id="element-126">
            Voglio l'archivio
            <ArrowRightIcon className="w-4 h-4" strokeWidth={3} data-id="element-127" />
          </a>
        </div>
      </div>
    </section>;
}